import { db } from "./dbClient";

// ─── SARPage.jsx ──────────────────────────────────────────────────────────────
// SAR Filing Helper — draft, manage, and file Suspicious Activity Reports.
// AI-drafts the narrative from linked case data, supports download and filing links.

import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useTheme } from "./theme";
import { useAITracker } from "./useAITracker";
import { useAuditLog } from "./useAuditLog";
import { downloadCSV, printToPDF, downloadXML, generateSARXML, generateGoAMLXML, printSARFormPDF, sanitize } from "./exportUtils";
import { SkeletonTable } from "./Skeleton";
import EmptyState from "./EmptyState";
import ConfirmDialog from "./ConfirmDialog";
import AuditLogPanel from "./AuditLogPanel";

// ─── Filing links by jurisdiction ─────────────────────────────────────────────
const FILING_LINKS = {
  US:  { label: "File on FinCEN BSA E-Filing",  url: "https://bsaefiling.fincen.treas.gov/main.html" },
  CA:  { label: "File on FINTRAC",              url: "https://www.fintrac-canafe.gc.ca/reporting-declaration/Info/rpt-eng" },
  UK:  { label: "File on NCA SARs Online",      url: "https://www.nationalcrimeagency.gov.uk/what-we-do/crime-threats/money-laundering-and-terrorist-financing/ukfiu" },
  AU:  { label: "File on AUSTRAC",              url: "https://www.austrac.gov.au/business/core-compliance/reporting" },
  EU:  { label: "File with your national FIU",  url: "https://www.egmont.org/members/" },
  SG:  { label: "File on STR (MAS)",            url: "https://www.mas.gov.sg/regulation/anti-money-laundering" },
  AE:  { label: "File on goAML (CBUAE)",        url: "https://www.centralbank.ae/en/node/1671" },
};
const GENERIC_FILING = { label: "FATF Filing Guidance", url: "https://www.fatf-gafi.org/en/topics/financial-intelligence.html" };

const SUSPICIOUS_TYPES = [
  "Structuring / Smurfing",
  "Layering",
  "Cash-intensive business",
  "Funnel account",
  "Trade-based ML",
  "Real estate ML",
  "Crypto / virtual asset abuse",
  "Shell company",
  "PEP / sanctions link",
  "Unusual wire transfers",
  "Rapid movement of funds",
  "Dormant account activity",
  "Inconsistent business profile",
  "Other",
];

const emptyForm = () => ({
  case_id: "",
  filing_institution: "",
  subject_name: "",
  subject_dob: "",
  subject_address: "",
  subject_id_type: "",
  subject_id_number: "",
  subject_is_victim: "",
  account_number: "",
  transaction_dates: "",
  counterparty_name: "",
  counterparty_dob: "",
  counterparty_address: "",
  counterparty_id_type: "",
  counterparty_id_number: "",
  counterparty_is_victim: "",
  amount: "",
  currency: "USD",
  narrative: "",
  suspicious_activity_type: [],
  jurisdiction: "",
  filed_ref: "",
  status: "draft",
  detection_date: "",
  has_suspect: true,
});

// ─── BSA Deadline helpers ──────────────────────────────────────────────────────
function computeDeadline(detectionDate, hasSuspect) {
  if (!detectionDate) return null;
  const d = new Date(detectionDate);
  if (isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + (hasSuspect ? 30 : 60));
  return d;
}

function deadlineBadge(deadline, C) {
  if (!deadline) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
  let bg, color, text;
  if (diff < 0) {
    bg = `${C.danger}22`; color = C.danger;
    text = `⚠ OVERDUE`;
  } else if (diff <= 5) {
    bg = `${C.danger}22`; color = C.danger;
    text = `⚠ ${diff} DAY${diff !== 1 ? "S" : ""} LEFT`;
  } else if (diff <= 14) {
    bg = `${C.warn}22`; color = C.warn;
    text = `⏱ ${diff} DAYS LEFT`;
  } else {
    bg = `${C.success}22`; color = C.success;
    text = `✓ ${diff} DAYS TO DEADLINE`;
  }
  return { bg, color, text, diff };
}

// ─── Label + input helpers ────────────────────────────────────────────────────
function Field({ label, children, half }) {
  const C = useTheme();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5,
      gridColumn: half ? "span 1" : "span 2" }}>
      <label style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em" }}>
        {label.toUpperCase()}
      </label>
      {children}
    </div>
  );
}

// ─── SAR Form ─────────────────────────────────────────────────────────────────
function SARForm({ draft, onSave, onCancel, alerts, company, currentUser, team }) {
  const C = useTheme();
  const { t } = useTranslation();
  const inputStyle = {
    background: "rgba(0,0,0,0.2)", border: `1px solid ${C.border}`, borderRadius: 7,
    color: C.text, fontSize: 13, padding: "8px 12px",
    fontFamily: "'DM Mono',monospace", outline: "none", width: "100%", boxSizing: "border-box",
  };
  // Initialize form: if draft has new JSONB structure, extract data field
  const initializeForm = () => {
    if (!draft) return emptyForm();

    // Handle new JSONB format where form data is in the 'data' field
    if (draft.data && typeof draft.data === 'object') {
      const formData = { ...draft.data };
      // Ensure suspicious_activity_type is always an array
      if (formData.suspicious_activity_type && !Array.isArray(formData.suspicious_activity_type)) {
        formData.suspicious_activity_type = [];
      }
      return formData;
    }

    // Handle legacy format where fields are directly on draft
    const legacyForm = { ...draft };
    if (!legacyForm.suspicious_activity_type) {
      legacyForm.suspicious_activity_type = [];
    } else if (!Array.isArray(legacyForm.suspicious_activity_type)) {
      legacyForm.suspicious_activity_type = [];
    }
    return legacyForm;
  };

  const [form, setForm] = useState(initializeForm());
  const [draftingAI,      setDraftingAI]      = useState(false);
  const [saving,          setSaving]          = useState(false);
  const [aiError,         setAiError]         = useState("");
  const [checkingQuality, setCheckingQuality] = useState(false);
  const [qualityResult,   setQualityResult]   = useState(null);
  const [saveMsg,         setSaveMsg]         = useState("");
  const [duplicateWarning, setDuplicateWarning] = useState(null); // E-002
  const [overdueAcknowledged, setOverdueAcknowledged] = useState(false); // F-011 (legacy, kept for compat)
  const [mlroDeadlineAck, setMlroDeadlineAck] = useState(false); // ITEM 1: hard gate
  const [showDeadlineModal, setShowDeadlineModal] = useState(false); // ITEM 1: modal
  const [deadlineModalConfirm, setDeadlineModalConfirm] = useState(false); // ITEM 1: checkbox
  const [mlroQualityOverride, setMlroQualityOverride] = useState(false); // ITEM 12: quality override

  const { callAI } = useAITracker(company, currentUser);
  const { log }    = useAuditLog(company, currentUser);

  const sarType = draft?.report_type === "CAR" ? "CAR" : draft?.amendment_of ? "amendment" : "sar";
  const parentSarRef = draft?.parent_sar_id || draft?.amendment_of;

  function set(key, val) { setForm(f => ({ ...f, [key]: val })); }

  function toggleSuspiciousType(typ) {
    setForm(f => ({
      ...f,
      suspicious_activity_type: (f.suspicious_activity_type || []).includes(typ)
        ? (f.suspicious_activity_type || []).filter(x => x !== typ)
        : [...(f.suspicious_activity_type || []), typ],
    }));
  }

  async function draftNarrative() {
    setDraftingAI(true); setAiError("");
    const linkedCase = alerts.find(a => a.id === form.case_id);
    try {
      const text = await callAI({
        system: `You are an AML compliance officer writing a Suspicious Activity Report narrative.
Write a concise, factual, third-person narrative for regulatory submission.
Use formal regulatory language. Do not include opinions or subjective language.
Structure: 3-5 paragraphs covering (1) subject identity, (2) transactions and behaviour observed,
(3) why it is suspicious, (4) patterns identified, (5) what further action is recommended.
Be specific about amounts, dates, and transaction types where provided.`,
        messages: [{ role: "user", content: `
SAR Details:
Filing institution: ${form.filing_institution || company?.name || "Not specified"}
Subject name: ${form.subject_name || "Unknown"}
Subject ID type: ${form.subject_id_type || "Unknown"}
Account number: ${form.account_number || "Unknown"}
Amount: ${form.amount} ${form.currency}
Transaction dates: ${form.transaction_dates || "See case record"}
Suspicious activity types: ${form.suspicious_activity_type.join(", ") || "To be determined"}
Jurisdiction: ${form.jurisdiction || company?.jurisdiction || "Unknown"}
${linkedCase ? `
Linked case:
  Entity: ${linkedCase.entity}
  Alert type: ${linkedCase.type}
  Risk level: ${linkedCase.risk}
  Status: ${linkedCase.status}
  Notes: ${linkedCase.notes || "None"}
` : ""}
Draft a professional SAR narrative based on the above information.` }],
        maxTokens: 1200,
        feature: "sar_draft",
      });
      set("narrative", text.trim());
    } catch (e) {
      setAiError(t('sar.form.aiError'));
    }
    setDraftingAI(false);
  }

  async function checkNarrativeQuality() {
    if (!form.narrative.trim()) return;
    setCheckingQuality(true); setQualityResult(null);
    try {
      const raw = await callAI({
        system: `You are a BSA compliance expert reviewing a SAR narrative before filing.
Check the narrative against the 5W+H framework required by FinCEN:
- WHO: Is the subject identified with sufficient detail?
- WHAT: Is the suspicious activity clearly described?
- WHEN: Are specific dates or timeframes included?
- WHERE: Is a location or account identified?
- WHY: Is there a clear explanation of why it's suspicious?
- HOW: Is the method or mechanism of the suspicious activity explained?

For each element, return: PRESENT, PARTIAL, or MISSING.
Then give an OVERALL: PASS (all present), REVIEW (some partial/missing), or FAIL (major gaps).
Format your response as JSON: {"who":"PRESENT","what":"PRESENT","when":"PARTIAL","where":"MISSING","why":"PRESENT","how":"PARTIAL","overall":"REVIEW","notes":"Brief explanation of gaps"}`,
        messages: [{ role: "user", content: `Review this SAR narrative:\n\n${form.narrative}` }],
        maxTokens: 400,
        feature: "sar_quality_check",
      });
      try {
        const jsonMatch = raw.match(/\{[\s\S]*\}/);
        setQualityResult({ parsed: JSON.parse(jsonMatch ? jsonMatch[0] : raw) });
      } catch {
        setQualityResult({ raw });
      }
    } catch (e) {
      setQualityResult({ error: "Quality check failed — please try again." });
    }
    setCheckingQuality(false);
  }

  // Lightweight update — only touches status + extra fields (filing_date, filed_ref, etc.)
  // Used by CO approval actions to avoid sending form fields that may not match DB schema
  async function updateStatus(newStatus) {
    setSaving(true);
    const payload = {
      status:     newStatus,
      updated_at: new Date().toISOString(),
    };
    const { data, error } = await db?.from("sar_drafts").update(payload).eq("id", draft.id).single().execute();
    if (error) {
      setSaveMsg(`⚠ Save failed: ${error.message}`);
      setSaving(false);
      return; // keep form open so user sees the error
    }
    setSaveMsg("✓ Saved");
    const result = data || { ...draft, ...payload, id: draft.id };
    await log({ action:"sar_updated", module:"sar", entityId:draft.id,
      entityName:form.subject_name, newValue:{ status:newStatus } });
    setSaving(false);
    onSave(result);
  }

  // E-002 / F-013: Duplicate detection
  async function checkDuplicate() {
    if (!form.subject_name || !company?.id) return false;
    const { data } = await db?.from("sar_drafts")
      .select("id, subject_name, detection_date, status")
      .eq("company_id", company.id)
      .ilike("subject_name", form.subject_name.trim())
      .neq("status", "void");
    if (!data?.length) return false;
    const existing = data.filter(d => d.id !== draft?.id);
    if (existing.length > 0) {
      setDuplicateWarning(existing);
      return true;
    }
    return false;
  }

  async function loadCaseData(caseId) {
    if (!caseId || !company?.id) {
      console.log("[SAR] Missing caseId or company:", { caseId, company: company?.id });
      return;
    }
    try {
      console.log("[SAR] Loading case data for:", caseId);

      // Fetch case details
      const { data: caseDataRaw, error: caseError } = await db?.from("cases")
        .select("*")
        .eq("id", caseId)
        .eq("company_id", company.id)
        .single();

      console.log("[SAR] Case data result:", { caseDataRaw, caseError });

      // Handle case where .single() returns an array
      const caseData = Array.isArray(caseDataRaw) ? caseDataRaw[0] : caseDataRaw;

      if (caseError || !caseData) {
        console.error("[SAR] Failed to load case:", caseError);
        setSaveMsg(`⚠ Could not find case: ${caseError?.message || "Not found"}`);
        return;
      }

      console.log("[SAR] Case loaded:", caseData.entity);

      // Fetch related KYC submission
      const { data: kycData } = await db?.from("kyc_submissions")
        .select("*")
        .eq("company_id", company.id)
        .ilike("full_name", `%${caseData.entity}%`)
        .order("created_at", { ascending: false })
        .limit(1);

      // Fetch sanctions screening results
      let sanctionsData = null;
      if (kycData && kycData.length > 0) {
        const { data: sanctions } = await db?.from("sanctions_screening")
          .select("*")
          .eq("kyc_submission_id", kycData[0].id)
          .order("screening_date", { ascending: false })
          .limit(1);
        sanctionsData = sanctions;
      }

      // Fetch transactions related to this case
      const { data: transactions } = await db?.from("transactions")
        .select("*")
        .eq("company_id", company.id)
        .ilike("entity", `%${caseData.entity}%`)
        .order("created_at", { ascending: false });

      // Fetch alerts for this case entity
      const { data: caseAlerts } = await db?.from("tm_alerts")
        .select("*")
        .eq("company_id", company.id)
        .ilike("entity", `%${caseData.entity}%`)
        .order("created_at", { ascending: false });

      // Populate form fields
      console.log("[SAR] Populating form...");
      set("subject_name", caseData.entity || "");
      set("narrative", caseData.narrative || caseData.notes || "");
      set("investigation_notes", caseData.investigation_notes || "");

      if (kycData && kycData.length > 0) {
        console.log("[SAR] Found KYC data:", kycData[0]);
        set("account_number", kycData[0].account_number || "");
      }

      if (sanctionsData && sanctionsData.length > 0) {
        console.log("[SAR] Found sanctions data:", sanctionsData[0]);
        set("jurisdiction", sanctionsData[0].name_origin || form.jurisdiction);
      }

      // Build transaction summary for narrative
      let fullNarrative = caseData.narrative || caseData.notes || "";
      if (transactions && transactions.length > 0) {
        console.log("[SAR] Found transactions:", transactions.length);
        const txSummary = `
Linked Transactions (${transactions.length} total):
${transactions.slice(0, 10).map(tx =>
  `- ${tx.transaction_type}: ${tx.amount} ${tx.currency} on ${new Date(tx.created_at).toLocaleDateString()}`
).join('\n')}
${transactions.length > 10 ? `... and ${transactions.length - 10} more transactions` : ''}
        `.trim();
        fullNarrative = fullNarrative ? fullNarrative + "\n\n" + txSummary : txSummary;
      }

      // Add alerts info to narrative
      if (caseAlerts && caseAlerts.length > 0) {
        console.log("[SAR] Found alerts:", caseAlerts.length);
        const alertsSummary = `
Triggered Alerts (${caseAlerts.length}):
${caseAlerts.slice(0, 5).map(alert =>
  `- ${alert.alert_type} (${alert.severity}): ${alert.notes || alert.rule || ""}`
).join('\n')}
        `.trim();
        fullNarrative = fullNarrative + "\n\n" + alertsSummary;
      }

      set("narrative", fullNarrative);

      const msg = `✓ Case loaded: ${caseData.entity}`;
      console.log("[SAR]", msg);
      setSaveMsg(msg);
    } catch (err) {
      console.error("[SAR] Error loading case data:", err);
      setSaveMsg(`⚠ Error loading case: ${err.message}`);
    }
  }

  async function handleSave(newStatus, extraPayload = {}) {
    // E-002: Check for duplicates before saving new SARs (even drafts)
    if (!draft?.id) {
      const isDuplicate = await checkDuplicate();
      if (isDuplicate) {
        setSaveMsg("🚨 A SAR already exists for this subject. File a CAR instead if continuing the investigation.");
        setSaving(false);
        return;
      }
    }
    // #14: Watchlist check at SAR entry point
    if (form.subject_name && company?.id) {
      const { data: wlHits } = await db?.from("internal_watchlist")
        .select("full_name, list_type")
        .eq("company_id", company.id)
        .ilike("full_name", `%${form.subject_name.trim()}%`)
        .limit(3);
      if (wlHits && wlHits.length > 0) {
        const hitStr = wlHits.map(h => `${h.full_name} (${h.list_type})`).join(", ");
        setSaveMsg(`🚨 Watchlist hit: ${hitStr} — Noted in SAR narrative. Proceed with heightened scrutiny.`);
      }
    }
    setSaving(true);
    // Build payload for sar_drafts table
    // Only save fields that exist: title, status, company_id, data (JSONB)
    // E-041: Sanitize all free-text fields before saving
    const payload = {
      title: sanitize(form.subject_name) || "Untitled SAR",
      status: newStatus || form.status || "draft",
      company_id: company.id,
      data: {
        case_id:                  sanitize(form.case_id)       || null,
        filing_institution:       sanitize(form.filing_institution) || null,
        subject_name:             sanitize(form.subject_name)  || null,
        subject_dob:              form.subject_dob   || null,
        subject_address:          sanitize(form.subject_address) || null,
        subject_id_type:          sanitize(form.subject_id_type) || null,
        subject_id_number:        sanitize(form.subject_id_number) || null,
        subject_is_victim:        form.subject_is_victim || null,
        account_number:           sanitize(form.account_number) || null,
        transaction_dates:        sanitize(form.transaction_dates) || null,
        counterparty_name:        sanitize(form.counterparty_name) || null,
        counterparty_dob:         form.counterparty_dob || null,
        counterparty_address:     sanitize(form.counterparty_address) || null,
        counterparty_id_type:     sanitize(form.counterparty_id_type) || null,
        counterparty_id_number:   sanitize(form.counterparty_id_number) || null,
        counterparty_is_victim:   form.counterparty_is_victim || null,
        amount:                   form.amount ? parseFloat(form.amount) : null,
        currency:                 form.currency || "USD",
        narrative:                sanitize(form.narrative) || null,
        suspicious_activity_type: Array.isArray(form.suspicious_activity_type) ? form.suspicious_activity_type : [],
        jurisdiction:             form.jurisdiction  || null,
        filed_ref:                form.filed_ref     || null,
        detection_date:           form.detection_date || null,
        has_suspect:              form.has_suspect,
        amendment_of:             form.amendment_of || null,
        created_by:               currentUser.email,
        ...extraPayload,
      },
    };
    let result;
    if (draft?.id) {
      const { data, error } = await db?.from("sar_drafts").update(payload).eq("id", draft.id).single().execute();
      if (error) {
        setSaveMsg(`⚠ Save failed: ${error.message}`);
        setSaving(false);
        return; // keep form open — do NOT call onSave(null)
      }
      setSaveMsg("✓ Saved");
      result = data || { ...draft, ...payload, id: draft.id };
      await log({ action:"sar_updated", module:"sar", entityId:draft.id,
        entityName:form.subject_name, newValue:{ status:payload.status } });
    } else {
      const { data, error } = await db?.from("sar_drafts").insert(payload).single().execute();
      if (error) {
        setSaveMsg(`⚠ Save failed: ${error.message}`);
        setSaving(false);
        return;
      }
      result = data;
      await log({ action:"sar_created", module:"sar", entityId:data?.id,
        entityName:form.subject_name });
    }
    setSaving(false);
    onSave(result);
  }

  function handleDownload() {
    const jKey = (form.jurisdiction || company?.jurisdiction || "").toUpperCase().slice(0,2);
    const filingLink = FILING_LINKS[jKey] || GENERIC_FILING;
    printToPDF(`SAR — ${form.subject_name || "Subject"}`, `
      <div class="section">
        <div class="section-title">Filing Details</div>
        <table>
          <tbody>
            <tr><td style="width:35%;color:#4A5568">Filing Institution</td><td>${form.filing_institution || company?.name || "—"}</td></tr>
            <tr><td style="color:#4A5568">Jurisdiction</td><td>${form.jurisdiction || company?.jurisdiction || "—"}</td></tr>
            <tr><td style="color:#4A5568">SAR Status</td><td style="text-transform:uppercase;font-weight:700">${form.status}</td></tr>
            ${form.filed_ref ? `<tr><td style="color:#4A5568">Filing Reference</td><td>${form.filed_ref}</td></tr>` : ""}
          </tbody>
        </table>
      </div>
      <div class="section">
        <div class="section-title">Subject Information</div>
        <table>
          <tbody>
            <tr><td style="width:35%;color:#4A5568">Full Name</td><td>${form.subject_name || "—"}</td></tr>
            <tr><td style="color:#4A5568">Date of Birth</td><td>${form.subject_dob || "—"}</td></tr>
            <tr><td style="color:#4A5568">Address</td><td>${form.subject_address || "—"}</td></tr>
            <tr><td style="color:#4A5568">ID Type</td><td>${form.subject_id_type || "—"}</td></tr>
            <tr><td style="color:#4A5568">ID Number</td><td>${form.subject_id_number || "—"}</td></tr>
            <tr><td style="color:#4A5568">Account Number</td><td>${form.account_number || "—"}</td></tr>
            <tr><td style="color:#4A5568">Is Customer a Victim?</td><td>${form.subject_is_victim ? form.subject_is_victim.charAt(0).toUpperCase() + form.subject_is_victim.slice(1) : "—"}</td></tr>
          </tbody>
        </table>
      </div>
      ${form.counterparty_name ? `<div class="section">
        <div class="section-title">Counterparty Information</div>
        <table>
          <tbody>
            <tr><td style="width:35%;color:#4A5568">Full Name</td><td>${form.counterparty_name || "—"}</td></tr>
            <tr><td style="color:#4A5568">Date of Birth</td><td>${form.counterparty_dob || "—"}</td></tr>
            <tr><td style="color:#4A5568">Address</td><td>${form.counterparty_address || "—"}</td></tr>
            <tr><td style="color:#4A5568">ID Type</td><td>${form.counterparty_id_type || "—"}</td></tr>
            <tr><td style="color:#4A5568">ID Number</td><td>${form.counterparty_id_number || "—"}</td></tr>
            <tr><td style="color:#4A5568">Is Counterparty a Victim?</td><td>${form.counterparty_is_victim ? form.counterparty_is_victim.charAt(0).toUpperCase() + form.counterparty_is_victim.slice(1) : "—"}</td></tr>
          </tbody>
        </table>
      </div>` : ""}
      <div class="section">
        <div class="section-title">Transaction Details</div>
        <table>
          <tbody>
            <tr><td style="width:35%;color:#4A5568">Amount</td><td>${form.amount || "—"} ${form.currency}</td></tr>
            <tr><td style="color:#4A5568">Transaction Dates</td><td>${form.transaction_dates || "—"}</td></tr>
            <tr><td style="color:#4A5568">Suspicious Activity</td><td>${(form.suspicious_activity_type||[]).join(", ") || "—"}</td></tr>
          </tbody>
        </table>
      </div>
      <div class="section">
        <div class="section-title">Narrative</div>
        <p style="font-size:11px;line-height:1.8;white-space:pre-wrap">${form.narrative || "No narrative yet."}</p>
      </div>
      <div class="section">
        <div class="section-title">Filing Link</div>
        <p style="font-size:11px">${filingLink.label}: <strong>${filingLink.url}</strong></p>
      </div>
      ${form.case_id ? `<div class="section"><div class="section-title">Case Reference</div><table><tbody><tr><td style="width:35%;color:#4A5568">Linked Case ID</td><td>${form.case_id}</td></tr></tbody></table></div>` : ""}
      ${qualityResult?.parsed ? `<div class="section">
        <div class="section-title">AI Narrative Quality Check (5W+H)</div>
        <table><tbody>
          <tr><td style="width:35%;color:#4A5568;font-weight:700">Overall Result</td><td style="font-weight:700;color:${qualityResult.parsed.overall==='PASS'?'#276749':qualityResult.parsed.overall==='REVIEW'?'#744210':'#C53030'}">${qualityResult.parsed.overall}</td></tr>
          ${['who','what','when','where','why','how'].map(el=>`<tr><td style="color:#4A5568">${el.toUpperCase()}</td><td>${(qualityResult.parsed[el]||'MISSING').toUpperCase()}</td></tr>`).join('')}
          ${qualityResult.parsed.notes?`<tr><td style="color:#4A5568">Notes</td><td>${qualityResult.parsed.notes}</td></tr>`:''}
        </tbody></table>
      </div>` : ""}`,
      company?.name
    );
  }

  function handleBSAXML() {
    const xml = generateSARXML(form, company);
    downloadXML(xml, `SAR_BSA_${(form.subject_name || "subject").replace(/\s+/g,"_")}_${Date.now()}.xml`);
  }

  function handleGoAMLXML() {
    const xml = generateGoAMLXML(form, company);
    downloadXML(xml, `SAR_goAML_${(form.subject_name || "subject").replace(/\s+/g,"_")}_${Date.now()}.xml`);
  }

  function handleOfficialPDF() {
    printSARFormPDF(form, company);
  }

  async function handleSubmitForReview() {
    setSaving(true);
    const draftToUpdate = draft || editing;
    if (!draftToUpdate?.id || !company?.id) {
      setSaving(false);
      return;
    }
    try {
      const { error } = await db?.from("sar_drafts")
        .update({ status: "review", updated_at: new Date().toISOString() })
        .eq("id", draftToUpdate.id)
        .execute();
      if (error) {
        console.error('[SAR] Submit for review failed:', error);
        setSaving(false);
        return;
      }
      setForm(prev => ({ ...prev, status: "review" }));
      setSaveMsg("✓ Submitted for review");
      const cos = (team || []).filter(m => m.permission === "compliance_officer" || m.permission === "admin");
      for (const co of cos) {
        if (!co.id) continue;
        try {
          await db?.from("notifications").insert({
            company_id:  company.id,
            user_id:     co.user_id || co.id,
            type:        "sar_ready",
            title:       `SAR submitted for MLRO review: ${form.subject_name || "Unknown subject"}`,
            body:        `A SAR has been submitted for MLRO/CO review and requires approval before external filing.`,
            link_module: "sar",
            read:        false,
          }).execute();
        } catch (e) { console.warn("[sar] notify co failed:", e.message); }
      }
    } finally {
      setSaving(false);
    }
  }

  // E-034 / F-001: Submit to MLRO (internal escalation step)
  async function handleSubmitToMLRO() {
    setSaving(true);
    const draftToUpdate = draft || editing;
    if (!draftToUpdate?.id || !company?.id) {
      setSaving(false);
      return;
    }
    try {
      const { error } = await db?.from("sar_drafts")
        .update({ status: "mlro_review", updated_at: new Date().toISOString() })
        .eq("id", draftToUpdate.id)
        .execute();
      if (error) {
        console.error('[SAR] Submit to MLRO failed:', error);
        setSaving(false);
        return;
      }
      setForm(prev => ({ ...prev, status: "mlro_review" }));
      setSaveMsg("✓ Escalated to MLRO");
      const mlros = (team || []).filter(m => m.permission === "compliance_officer" || m.permission === "admin");
      for (const m of mlros) {
        if (!m.id) continue;
        try {
          await db?.from("notifications").insert({
            company_id:  company.id,
            user_id:     m.user_id || m.id,
            type:        "sar_mlro",
            title:       `SAR escalated to MLRO: ${form.subject_name || "Unknown subject"}`,
            body:        `A SAR has been escalated to the MLRO for a mandatory decision before external filing.`,
            link_module: "sar",
            read:        false,
          }).execute();
        } catch (e) { console.warn("[sar] mlro notify failed:", e.message); }
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleCOApprove() {
    await updateStatus("filed");
  }

  async function handleCODeny() {
    await updateStatus("void");
  }

  async function handleCORequestInfo() {
    await updateStatus("draft");
  }

  const isCO = currentUser?.permission === "compliance_officer" || currentUser?.permission === "admin";
  const isMlroOrAdmin = currentUser?.permission === "compliance_officer" || currentUser?.permission === "admin" || currentUser?.role === "mlro" || currentUser?.role === "admin";
  const isUnderReview = form.status === "review" || form.status === "mlro_review";
  const isMlroReview  = form.status === "mlro_review";

  // ITEM 12: Quality check gate — broader keyword matching
  const QUALITY_FAIL_WORDS = /fail|poor|insufficient|incomplete/i;
  const QUALITY_PASS_WORDS = /pass|good|adequate|sufficient/i;
  const qualityPassed = (() => {
    if (!qualityResult) return false;
    if (qualityResult.parsed) {
      const overall = qualityResult.parsed.overall || "";
      if (QUALITY_PASS_WORDS.test(overall)) return true;
      if (QUALITY_FAIL_WORDS.test(overall)) return false;
      // Check numeric score if present
      const score = qualityResult.parsed.score;
      if (typeof score === "number") return score >= 70;
      return false;
    }
    if (qualityResult.raw) {
      if (QUALITY_PASS_WORDS.test(qualityResult.raw)) return true;
      return false;
    }
    return false;
  })();
  const qualityFailed = (() => {
    if (!qualityResult) return false;
    if (qualityResult.parsed) {
      const overall = qualityResult.parsed.overall || "";
      if (QUALITY_FAIL_WORDS.test(overall)) return true;
      return false;
    }
    if (qualityResult.raw) return QUALITY_FAIL_WORDS.test(qualityResult.raw);
    return false;
  })();
  // Filing allowed when quality passed OR mlro override
  const qualityGateOk = qualityPassed || mlroQualityOverride;

  const jKey = (form.jurisdiction || company?.jurisdiction || "").toUpperCase().slice(0,2);
  const filingLink = FILING_LINKS[jKey] || GENERIC_FILING;
  const formDeadline = computeDeadline(form.detection_date, form.has_suspect);
  const formBadge    = deadlineBadge(formDeadline, C);

  // ITEM 1: Deadline hard gate — blocked until mlroDeadlineAck
  const isOverdue = formBadge && formBadge.diff < 0 && form.status !== "filed" && form.status !== "void";
  const overdueBlocked = isOverdue && !mlroDeadlineAck;

  async function handleDeadlineModalConfirm() {
    setMlroDeadlineAck(true);
    setShowDeadlineModal(false);
    setDeadlineModalConfirm(false);
    setOverdueAcknowledged(true); // keep legacy state in sync
    await log({
      action: "sar_deadline_breach_acknowledged",
      module: "sar",
      entityId: draft?.id,
      entityName: form.subject_name,
      meta: { deadline: formDeadline?.toISOString().slice(0, 10), breached: true },
    });
  }

  return (
    <div>
      {/* Form header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24 }}>
        <div>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <h1 style={{ color:C.text, fontFamily:"'Syne',sans-serif", fontSize:24,
              fontWeight:700, margin:0 }}>
              {draft?.id ? t('sar.form.editTitle') : t('sar.form.newTitle')}
            </h1>
            {form.report_type === "CAR" && (
              <span style={{ background:`${C.success}20`, color:C.success,
                border:`1px solid ${C.success}44`, borderRadius:6,
                padding:"4px 10px", fontSize:11, fontWeight:700,
                fontFamily:"'DM Mono',monospace", letterSpacing:"0.05em", whiteSpace:"nowrap" }}>
                CAR — Continuation Activity Report
              </span>
            )}
            {form.status === "amendment_draft" && (
              <span style={{ background:`${C.warn}20`, color:C.warn,
                border:`1px solid ${C.warn}44`, borderRadius:6,
                padding:"4px 10px", fontSize:11, fontWeight:700,
                fontFamily:"'DM Mono',monospace", letterSpacing:"0.05em", whiteSpace:"nowrap" }}>
                Amendment
              </span>
            )}
            {formBadge && !form.report_type && form.status !== "amendment_draft" && (
              <span style={{ background:formBadge.bg, color:formBadge.color,
                border:`1px solid ${formBadge.color}44`, borderRadius:6,
                padding:"4px 10px", fontSize:11, fontWeight:700,
                fontFamily:"'DM Mono',monospace", letterSpacing:"0.05em", whiteSpace:"nowrap" }}>
                {formBadge.text}
              </span>
            )}
          </div>
          <p style={{ color:C.textMid, margin:"4px 0 0", fontSize:13 }}>
            {t('sar.form.sarSubtitle')}
            {formDeadline && (
              <span style={{ marginLeft:8, color:C.textDim, fontSize:12 }}>
                · BSA deadline: {formDeadline.toISOString().slice(0,10)}
              </span>
            )}
          </p>
        </div>
        <button onClick={onCancel}
          style={{ background:"transparent", border:`1px solid ${C.border}`,
            borderRadius:7, color:C.textMid, padding:"8px 16px",
            cursor:"pointer", fontSize:13, fontFamily:"'DM Mono',monospace" }}>
          {t('sar.form.back')}
        </button>
      </div>

      {/* ITEM 1: Blocking banner for overdue SARs — hard gate, requires MLRO modal acknowledgment */}
      {isOverdue && (
        <div style={{ background:`${C.danger}15`, border:`2px solid ${C.danger}`, borderRadius:10, padding:"18px 20px", marginBottom:20 }}>
          <div style={{ display:"flex", alignItems:"flex-start", gap:14 }}>
            <span style={{ fontSize:24, flexShrink:0 }}>🚨</span>
            <div style={{ flex:1 }}>
              <div style={{ color:C.danger, fontWeight:700, fontSize:15, marginBottom:6 }}>
                REGULATORY FILING DEADLINE BREACHED — {Math.abs(formBadge.diff)} DAY{Math.abs(formBadge.diff) !== 1 ? "S" : ""} OVERDUE
              </div>
              <div style={{ color:C.textMid, fontSize:12, lineHeight:1.6, marginBottom:14 }}>
                This SAR has passed its mandatory {form.has_suspect ? "30-day (suspect known)" : "60-day (no suspect)"} filing deadline.
                Submission actions are <strong>blocked</strong> until the MLRO formally acknowledges
                this breach and authorizes late filing.
              </div>
              {!mlroDeadlineAck ? (
                <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                  <button onClick={() => { setDeadlineModalConfirm(false); setShowDeadlineModal(true); }}
                    style={{ padding:"8px 18px", background:C.danger, border:"none", borderRadius:7,
                      color:"#fff", cursor:"pointer", fontSize:13,
                      fontFamily:"'DM Mono',monospace", fontWeight:600 }}>
                    MLRO — Acknowledge Breach
                  </button>
                  <span style={{ color:C.textDim, fontSize:11 }}>Required before submission. Logged to audit trail.</span>
                </div>
              ) : (
                <div style={{ color:C.warn, fontSize:12, fontWeight:600 }}>
                  ✓ Breach acknowledged by MLRO — proceed with late filing immediately.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ITEM 1: MLRO Deadline Breach Acknowledgment Modal */}
      {showDeadlineModal && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:1000,
          display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:C.surface, border:`2px solid ${C.danger}`,
            borderRadius:12, padding:32, maxWidth:480, width:"90%", boxShadow:"0 8px 40px rgba(0,0,0,0.5)" }}>
            <div style={{ color:C.danger, fontWeight:700, fontSize:17, marginBottom:10 }}>
              MLRO — Deadline Breach Authorization
            </div>
            <div style={{ color:C.textMid, fontSize:13, lineHeight:1.7, marginBottom:20 }}>
              This SAR has breached its regulatory filing deadline. As MLRO, I acknowledge
              this breach and authorize late filing. This action will be permanently recorded
              in the compliance audit trail.
            </div>
            <label style={{ display:"flex", alignItems:"flex-start", gap:10, cursor:"pointer", marginBottom:24 }}>
              <input type="checkbox" checked={deadlineModalConfirm}
                onChange={e => setDeadlineModalConfirm(e.target.checked)}
                style={{ marginTop:2, accentColor:C.danger, width:16, height:16 }} />
              <span style={{ color:C.text, fontSize:13, lineHeight:1.6 }}>
                I confirm: I am the MLRO or authorized delegate. I acknowledge this SAR
                has breached its mandatory filing deadline of{" "}
                <strong>{formDeadline?.toISOString().slice(0,10)}</strong> and I authorize
                late filing under applicable escalation procedures.
              </span>
            </label>
            <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
              <button onClick={() => { setShowDeadlineModal(false); setDeadlineModalConfirm(false); }}
                style={{ padding:"9px 20px", background:"transparent",
                  border:`1px solid ${C.border}`, borderRadius:7,
                  color:C.textMid, fontSize:13, cursor:"pointer",
                  fontFamily:"'DM Mono',monospace" }}>
                Cancel
              </button>
              <button onClick={handleDeadlineModalConfirm} disabled={!deadlineModalConfirm}
                style={{ padding:"9px 20px", background: deadlineModalConfirm ? C.danger : `${C.danger}40`,
                  border:"none", borderRadius:7, color:"#fff", fontSize:13,
                  cursor: deadlineModalConfirm ? "pointer" : "not-allowed",
                  fontFamily:"'DM Mono',monospace", fontWeight:600 }}>
                Confirm — Authorize Late Filing
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ background:`${C.accent}08`, border:`1px solid ${C.accent}30`,
        borderRadius:8, padding:"10px 16px", marginBottom:20,
        display:"flex", gap:12, alignItems:"flex-start" }}>
        <span style={{ fontSize:16, flexShrink:0 }}>ℹ️</span>
        <div>
          <div style={{ color:C.text, fontSize:12, fontWeight:600, marginBottom:4 }}>
            Direct Filing Options
          </div>
          <div style={{ color:C.textMid, fontSize:11, lineHeight:1.6 }}>
            <strong>US (FinCEN):</strong> Download BSA XML → upload to{" "}
            <span style={{ color:C.accent }}>bsaefiling.fincen.treas.gov</span>
            {" · "}
            <strong>goAML jurisdictions</strong> (UAE, Nigeria, S. Africa, Kenya, Pakistan, Egypt):
            Download goAML XML → upload to your goAML portal
            {" · "}
            <strong>All others:</strong> Use Official Form 111 PDF → submit to your regulator
          </div>
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>

        {/* Section: Filing Details */}
        <div style={{ gridColumn:"span 2", background:C.surface, border:`1px solid ${C.border}`,
          borderRadius:10, padding:20 }}>
          <div style={{ color:C.text, fontWeight:600, fontSize:14, marginBottom:16 }}>
            {t('sar.form.sectionFiling')}
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <Field label={t('sar.fields.filingInstitution')} half>
              <input value={form.filing_institution} onChange={e=>set("filing_institution",e.target.value)}
                placeholder={company?.name || "Your institution name"}
                style={inputStyle} />
            </Field>
            <Field label={t('sar.fields.jurisdiction')} half>
              <select value={form.jurisdiction} onChange={e=>set("jurisdiction",e.target.value)}
                style={{ ...inputStyle }}>
                <option value="">{t('sar.form.selectJurisdiction')}</option>
                {Object.keys(FILING_LINKS).map(k=>(<option key={k} value={k}>{k}</option>))}
                <option value="OTHER">Other</option>
              </select>
            </Field>
            <Field label={t('sar.fields.caseId')} half>
              <select value={form.case_id} onChange={e=>{
                const caseId = e.target.value;
                set("case_id", caseId);
                if (caseId) loadCaseData(caseId);
              }}
                style={inputStyle}>
                <option value="">{t('sar.form.noLinkedCase')}</option>
                {alerts.filter(a => a.status === "escalated").slice(0,50).map(a=>(
                  <option key={a.id} value={a.id}>{a.entity} — {a.type} ({a.id.slice(0,8)})</option>
                ))}
              </select>
            </Field>
            <Field label={t('sar.fields.sarStatus')} half>
              <select value={form.status} onChange={e=>set("status",e.target.value)} style={inputStyle}>
                <option value="draft">{t('sar.form.statusDraft')}</option>
                <option value="review">{t('sar.form.statusUnderReview')}</option>
                <option value="filed">{t('sar.form.statusFiled')}</option>
                <option value="void">{t('sar.form.statusVoid')}</option>
              </select>
            </Field>
            {form.status === "filed" && (
              <Field label={t('sar.fields.filedRef')} half>
                <input value={form.filed_ref} onChange={e=>set("filed_ref",e.target.value)}
                  placeholder={t('sar.form.filingRefPlaceholder')}
                  style={inputStyle} />
              </Field>
            )}
            <Field label="Detection Date" half>
              <input type="date" value={form.detection_date}
                onChange={e=>set("detection_date",e.target.value)}
                style={{ ...inputStyle, colorScheme:"dark" }} />
            </Field>
            <Field label="Suspect Identified" half>
              <div style={{ display:"flex", alignItems:"center", gap:12, paddingTop:6 }}>
                <button onClick={()=>set("has_suspect",!form.has_suspect)}
                  style={{ display:"flex", alignItems:"center", gap:8, background:"transparent",
                    border:"none", cursor:"pointer", padding:0, fontFamily:"'DM Mono',monospace" }}>
                  <div style={{ width:36, height:20, borderRadius:10,
                    background: form.has_suspect ? C.accent : C.border,
                    position:"relative", transition:"background 0.2s" }}>
                    <div style={{ position:"absolute", top:3, left: form.has_suspect ? 18 : 3,
                      width:14, height:14, borderRadius:"50%",
                      background: form.has_suspect ? C.bg : C.textDim,
                      transition:"left 0.2s" }} />
                  </div>
                  <span style={{ color: form.has_suspect ? C.accent : C.textMid, fontSize:12 }}>
                    {form.has_suspect ? "Yes — 30-day deadline" : "No — 60-day deadline"}
                  </span>
                </button>
              </div>
            </Field>
          </div>
        </div>

        {/* Section: Amendment/CAR Details */}
        {(sarType === "amendment" || sarType === "CAR") && (
          <div style={{ gridColumn:"span 2", background:C.surface, border:`1px solid ${C.border}`,
            borderRadius:10, padding:20 }}>
            <div style={{ color:C.text, fontWeight:600, fontSize:14, marginBottom:16 }}>
              {sarType === "amendment" ? "Amendment Details" : "CAR (Continuation Activity Report) Details"}
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              {sarType === "amendment" && (
                <>
                  <Field label="Original SAR ID" half>
                    <input value={parentSarRef || ""} disabled style={{ ...inputStyle, opacity:0.6 }} />
                  </Field>
                  <Field label="Amendment Reason" half>
                    <input value={form.amendment_reason || ""}
                      onChange={e=>set("amendment_reason",e.target.value)}
                      placeholder="e.g., Correcting subject DOB, Adding transaction details"
                      style={inputStyle} />
                  </Field>
                  <div style={{ gridColumn:"span 2", color:C.textDim, fontSize:11, lineHeight:1.6 }}>
                    <strong>Amendment Guidance:</strong> Provide the specific reason for this amendment. Common reasons: correcting subject/counterparty information, adding omitted transactions, updating entity relationships, or adding new suspicious indicators discovered after initial filing.
                  </div>
                </>
              )}
              {sarType === "CAR" && (
                <>
                  <Field label="Original SAR ID" half>
                    <input value={parentSarRef || ""} disabled style={{ ...inputStyle, opacity:0.6 }} />
                  </Field>
                  <Field label="Related Transaction Date" half>
                    <input type="date" value={form.transaction_dates || ""}
                      onChange={e=>set("transaction_dates",e.target.value)}
                      style={{ ...inputStyle, colorScheme:"dark" }} />
                  </Field>
                  <div style={{ gridColumn:"span 2", color:C.textDim, fontSize:11, lineHeight:1.6 }}>
                    <strong>CAR Guidance:</strong> A CAR reports additional suspicious activity related to an original SAR. Include new transactions, updated entity information, or newly discovered typologies. Reference the original SAR filing reference in your narrative.
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Section: Subject Information */}
        <div style={{ gridColumn:"span 2", background:C.surface, border:`1px solid ${C.border}`,
          borderRadius:10, padding:20 }}>
          <div style={{ color:C.text, fontWeight:600, fontSize:14, marginBottom:16 }}>
            {t('sar.form.sectionSubject')}
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <Field label={t('sar.fields.subjectName')} half>
              <input value={form.subject_name} onChange={e=>set("subject_name",e.target.value)}
                placeholder={t('sar.form.subjectNamePlaceholder')} style={inputStyle} />
            </Field>
            <Field label={t('sar.fields.subjectDob')} half>
              <input type="date" value={form.subject_dob} onChange={e=>set("subject_dob",e.target.value)}
                style={{ ...inputStyle, colorScheme:"dark" }} />
            </Field>
            <Field label={t('sar.fields.subjectAddress')}>
              <input value={form.subject_address} onChange={e=>set("subject_address",e.target.value)}
                placeholder={t('sar.form.addressPlaceholder')} style={inputStyle} />
            </Field>
            <Field label={t('sar.fields.subjectIdType')} half>
              <select value={form.subject_id_type} onChange={e=>set("subject_id_type",e.target.value)}
                style={inputStyle}>
                <option value="">Select…</option>
                {["Passport","National ID","Driver's License","Tax ID","Other"].map(idType=>(
                  <option key={idType} value={idType}>{idType}</option>
                ))}
              </select>
            </Field>
            <Field label={t('sar.fields.subjectIdNumber')} half>
              <input value={form.subject_id_number} onChange={e=>set("subject_id_number",e.target.value)}
                placeholder={t('sar.form.idNumberPlaceholder')} style={inputStyle} />
            </Field>
            <Field label={t('sar.fields.accountNumber')} half>
              <input value={form.account_number} onChange={e=>set("account_number",e.target.value)}
                placeholder={t('sar.form.accountPlaceholder')} style={inputStyle} />
            </Field>
            <Field label="Is Customer a Victim?" half>
              <select value={form.subject_is_victim} onChange={e=>set("subject_is_victim",e.target.value)}
                style={inputStyle}>
                <option value="">Select…</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
                <option value="unsure">Unsure</option>
              </select>
            </Field>
          </div>
        </div>

        {/* Section: Counterparty Information */}
        <div style={{ gridColumn:"span 2", background:C.surface, border:`1px solid ${C.border}`,
          borderRadius:10, padding:20 }}>
          <div style={{ color:C.text, fontWeight:600, fontSize:14, marginBottom:16 }}>
            Counterparty Information
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <Field label="Counterparty Name" half>
              <input value={form.counterparty_name} onChange={e=>set("counterparty_name",e.target.value)}
                placeholder="Full name of counterparty" style={inputStyle} />
            </Field>
            <Field label="Counterparty Date of Birth" half>
              <input type="date" value={form.counterparty_dob} onChange={e=>set("counterparty_dob",e.target.value)}
                style={{ ...inputStyle, colorScheme:"dark" }} />
            </Field>
            <Field label="Counterparty Address">
              <input value={form.counterparty_address} onChange={e=>set("counterparty_address",e.target.value)}
                placeholder="Street address, city, state, ZIP, country" style={inputStyle} />
            </Field>
            <Field label="Counterparty ID Type" half>
              <select value={form.counterparty_id_type} onChange={e=>set("counterparty_id_type",e.target.value)}
                style={inputStyle}>
                <option value="">Select…</option>
                {["Passport","National ID","Driver's License","Tax ID","Other"].map(idType=>(
                  <option key={idType} value={idType}>{idType}</option>
                ))}
              </select>
            </Field>
            <Field label="Counterparty ID Number" half>
              <input value={form.counterparty_id_number} onChange={e=>set("counterparty_id_number",e.target.value)}
                placeholder="Identification number" style={inputStyle} />
            </Field>
            <Field label="Is Counterparty a Victim?" half>
              <select value={form.counterparty_is_victim} onChange={e=>set("counterparty_is_victim",e.target.value)}
                style={inputStyle}>
                <option value="">Select…</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
                <option value="unsure">Unsure</option>
              </select>
            </Field>
          </div>
        </div>

        {/* Section: Transaction Details */}
        <div style={{ gridColumn:"span 2", background:C.surface, border:`1px solid ${C.border}`,
          borderRadius:10, padding:20 }}>
          <div style={{ color:C.text, fontWeight:600, fontSize:14, marginBottom:16 }}>
            {t('sar.form.sectionTransaction')}
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <Field label={t('sar.fields.amount')} half>
              <input type="number" value={form.amount} onChange={e=>set("amount",e.target.value)}
                placeholder="0.00" style={inputStyle} />
            </Field>
            <Field label={t('sar.fields.currency')} half>
              <select value={form.currency} onChange={e=>set("currency",e.target.value)} style={inputStyle}>
                {["USD","EUR","GBP","CAD","AUD","SGD","AED","BTC","ETH","USDT"].map(curr=>(
                  <option key={curr} value={curr}>{curr}</option>
                ))}
              </select>
            </Field>
            <Field label={t('sar.fields.transactionDates')}>
              <input value={form.transaction_dates} onChange={e=>set("transaction_dates",e.target.value)}
                placeholder={t('sar.form.txDatesPlaceholder')}
                style={inputStyle} />
            </Field>
          </div>
        </div>

        {/* Section: Suspicious Activity */}
        <div style={{ gridColumn:"span 2", background:C.surface, border:`1px solid ${C.border}`,
          borderRadius:10, padding:20 }}>
          <div style={{ color:C.text, fontWeight:600, fontSize:14, marginBottom:14 }}>
            {t('sar.form.sectionSuspicious')}
          </div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
            {SUSPICIOUS_TYPES.map(typ => {
              const selected = form.suspicious_activity_type.includes(typ);
              return (
                <button key={typ} onClick={()=>toggleSuspiciousType(typ)}
                  style={{ padding:"6px 12px", borderRadius:6, fontSize:12, cursor:"pointer",
                    fontFamily:"'DM Mono',monospace",
                    background: selected ? C.accentGlow : "transparent",
                    border: `1px solid ${selected ? C.accent : C.border}`,
                    color: selected ? C.accent : C.textMid }}>
                  {selected ? "✓ " : ""}{typ}
                </button>
              );
            })}
          </div>
        </div>

        {/* Section: Narrative */}
        <div style={{ gridColumn:"span 2", background:C.surface, border:`1px solid ${C.border}`,
          borderRadius:10, padding:20 }}>
          <div style={{ display:"flex", justifyContent:"space-between",
            alignItems:"center", marginBottom:14 }}>
            <div style={{ color:C.text, fontWeight:600, fontSize:14 }}>{t('sar.form.sectionNarrative')}</div>
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={checkNarrativeQuality} disabled={checkingQuality || !form.narrative.trim()}
                style={{ background: checkingQuality ? C.border : `${C.success}22`,
                  border: `1px solid ${checkingQuality ? C.border : C.success}`,
                  borderRadius:7, color: checkingQuality ? C.textDim : C.success,
                  padding:"7px 16px", cursor: (checkingQuality||!form.narrative.trim()) ? "not-allowed" : "pointer",
                  fontSize:12, fontFamily:"'DM Mono',monospace",
                  display:"flex", alignItems:"center", gap:8 }}>
                {checkingQuality ? "Checking…" : "✓ Check Quality"}
              </button>
              <button onClick={draftNarrative} disabled={draftingAI}
                style={{ background: draftingAI ? C.border : C.accentGlow,
                  border: `1px solid ${C.accent}`, borderRadius:7, color:C.accent,
                  padding:"7px 16px", cursor:draftingAI?"wait":"pointer",
                  fontSize:12, fontFamily:"'DM Mono',monospace",
                  display:"flex", alignItems:"center", gap:8 }}>
                {draftingAI ? t('sar.form.aiDrafting') : t('sar.form.aiDraftBtn')}
              </button>
            </div>
          </div>
          {aiError && (
            <div style={{ color:C.danger, fontSize:12, marginBottom:10,
              background:`${C.danger}12`, padding:"8px 12px", borderRadius:6 }}>
              {aiError}
            </div>
          )}
          <textarea
            value={form.narrative}
            onChange={e=>{ set("narrative",e.target.value); setQualityResult(null); }}
            placeholder={t('sar.form.narrativePlaceholder')}
            style={{ ...inputStyle, minHeight:220, resize:"vertical", lineHeight:1.7 }}
          />
          <div style={{ color:C.textDim, fontSize:10, marginTop:6 }}>
            {t('sar.form.charCount', { count: form.narrative.length })}
          </div>
          {/* Quality check result panel */}
          {qualityResult && (
            <div style={{ marginTop:12, background:C.bg, border:`1px solid ${C.border}`,
              borderRadius:8, padding:16 }}>
              {qualityResult.error ? (
                <div style={{ color:C.danger, fontSize:12 }}>{qualityResult.error}</div>
              ) : qualityResult.raw ? (
                <div style={{ color:C.textMid, fontSize:12, whiteSpace:"pre-wrap" }}>{qualityResult.raw}</div>
              ) : qualityResult.parsed ? (() => {
                const p = qualityResult.parsed;
                const ELEMENTS = ["who","what","when","where","why","how"];
                const statusStyle = (val) => {
                  if (val === "PRESENT") return { bg:`${C.success}22`, color:C.success, icon:"✓" };
                  if (val === "PARTIAL") return { bg:`${C.warn}22`,    color:C.warn,    icon:"~" };
                  return                         { bg:`${C.danger}22`, color:C.danger,  icon:"✗" };
                };
                const overallStyle = p.overall === "PASS"
                  ? { bg:`${C.success}22`, color:C.success, label:"PASS" }
                  : p.overall === "REVIEW"
                  ? { bg:`${C.warn}22`,    color:C.warn,    label:"REVIEW" }
                  : { bg:`${C.danger}22`,  color:C.danger,  label:"FAIL" };
                return (
                  <div>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
                      <span style={{ color:C.textMid, fontSize:11, fontFamily:"'DM Mono',monospace",
                        letterSpacing:"0.1em" }}>5W+H QUALITY CHECK</span>
                      <span style={{ background:overallStyle.bg, color:overallStyle.color,
                        border:`1px solid ${overallStyle.color}44`, borderRadius:5,
                        padding:"3px 10px", fontSize:11, fontWeight:700,
                        fontFamily:"'DM Mono',monospace" }}>
                        {overallStyle.label}
                      </span>
                    </div>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:10 }}>
                      {ELEMENTS.map(el => {
                        const val = (p[el] || "MISSING").toUpperCase();
                        const st = statusStyle(val);
                        return (
                          <span key={el} style={{ background:st.bg, color:st.color,
                            border:`1px solid ${st.color}44`, borderRadius:5,
                            padding:"4px 10px", fontSize:11, fontFamily:"'DM Mono',monospace",
                            fontWeight:600 }}>
                            {st.icon} {el.toUpperCase()}: {val}
                          </span>
                        );
                      })}
                    </div>
                    {p.notes && (
                      <div style={{ color:C.textMid, fontSize:12, lineHeight:1.6,
                        borderTop:`1px solid ${C.border}`, paddingTop:10 }}>
                        {p.notes}
                      </div>
                    )}
                  </div>
                );
              })() : null}
            </div>
          )}
        </div>

        {/* Section: Filing Links */}
        <div style={{ gridColumn:"span 2", background:C.surface, border:`1px solid ${C.border}`,
          borderRadius:10, padding:20 }}>
          <div style={{ color:C.text, fontWeight:600, fontSize:14, marginBottom:14 }}>
            {t('sar.form.sectionFilingLinks')}
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            <a href={filingLink.url} target="_blank" rel="noopener noreferrer"
              style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px",
                background:C.accentGlow, border:`1px solid ${C.accent}`,
                borderRadius:8, color:C.accent, textDecoration:"none", fontSize:13 }}>
              <span style={{ fontSize:16 }}>◉</span>
              <div>
                <div style={{ fontWeight:600 }}>{filingLink.label}</div>
                <div style={{ color:C.textMid, fontSize:11, marginTop:2 }}>{filingLink.url}</div>
              </div>
              <span style={{ marginLeft:"auto", fontSize:12 }}>↗</span>
            </a>
            {/* Always show additional reference links */}
            {[
              { label:"FATF Filing Guidance",   url:"https://www.fatf-gafi.org/en/topics/financial-intelligence.html" },
              { label:"EGMONT FIU Directory",   url:"https://egmont.org/members/" },
            ].filter(l=>l.url!==filingLink.url).map(l=>(
              <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer"
                style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 14px",
                  background:"transparent", border:`1px solid ${C.border}`,
                  borderRadius:8, color:C.textMid, textDecoration:"none", fontSize:12 }}>
                {l.label}
                <span style={{ marginLeft:"auto", fontSize:11 }}>↗</span>
              </a>
            ))}
          </div>
        </div>
      </div>

      {saveMsg && (
        <div style={{ padding: "8px 28px", fontSize: 12, fontFamily: "'DM Mono', monospace",
          color: saveMsg.startsWith("⚠") ? "#FF4444" : "#00E676" }}>
          {saveMsg}
        </div>
      )}

      {/* E-002 / F-013: Duplicate warning */}
      {duplicateWarning && (
        <div style={{ margin:"0 0 16px", padding:"12px 16px",
          background:`${C.warn}15`, border:`1px solid ${C.warn}55`,
          borderRadius:8, color:C.warn, fontSize:12 }}>
          <div style={{ fontWeight:700, marginBottom:6 }}>
            ⚠ Possible duplicate SAR detected
          </div>
          <div style={{ color:C.textMid, marginBottom:8 }}>
            An existing SAR for <strong>{form.subject_name}</strong> was found:
          </div>
          {duplicateWarning.map(d => (
            <div key={d.id} style={{ fontSize:11, color:C.textDim, marginBottom:3 }}>
              · {d.id.slice(0,8)}… — detection: {d.detection_date || "unknown"} — status: {d.status}
            </div>
          ))}
          <div style={{ display:"flex", gap:8, marginTop:10 }}>
            <button onClick={() => { setDuplicateWarning(null); handleSave("draft"); }}
              style={{ background:"transparent", border:`1px solid ${C.warn}`, borderRadius:5,
                color:C.warn, fontSize:11, padding:"4px 12px", cursor:"pointer", fontFamily:"inherit" }}>
              Save as Draft Anyway
            </button>
            <button onClick={() => setDuplicateWarning(null)}
              style={{ background:"transparent", border:`1px solid ${C.border}`, borderRadius:5,
                color:C.textMid, fontSize:11, padding:"4px 12px", cursor:"pointer", fontFamily:"inherit" }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ITEM 12: Quality gate warning */}
      {(!qualityResult || qualityFailed) && (
        <div style={{ margin:"16px 0 0", padding:"12px 16px",
          background:`${C.warn}15`, border:`1px solid ${C.warn}55`,
          borderRadius:8, color:C.warn, fontSize:12 }}>
          <div style={{ fontWeight:700, marginBottom:4 }}>
            {!qualityResult
              ? "Quality check required before filing"
              : "Quality check failed — filing blocked"}
          </div>
          <div style={{ color:C.textMid, lineHeight:1.6 }}>
            SAR narrative must pass quality check before filing. Run quality check above.
          </div>
          {/* ITEM 12: MLRO quality override — visible to mlro/admin only */}
          {isMlroOrAdmin && (
            <label style={{ display:"flex", alignItems:"center", gap:8, marginTop:10,
              cursor:"pointer", color:C.textMid, fontSize:11 }}>
              <input type="checkbox" checked={mlroQualityOverride}
                onChange={e => setMlroQualityOverride(e.target.checked)}
                style={{ accentColor:C.warn, width:13, height:13 }} />
              Override quality gate (MLRO only) — waives narrative quality requirement
            </label>
          )}
        </div>
      )}
      {/* ITEM 12: Show override checkbox even when quality passed, for MLRO visibility */}
      {qualityResult && qualityPassed && isMlroOrAdmin && mlroQualityOverride && (
        <div style={{ margin:"12px 0 0", padding:"8px 14px",
          background:`${C.warn}10`, border:`1px solid ${C.warn}33`,
          borderRadius:7, color:C.warn, fontSize:11, display:"flex", alignItems:"center", gap:8 }}>
          <input type="checkbox" checked={mlroQualityOverride}
            onChange={e => setMlroQualityOverride(e.target.checked)}
            style={{ accentColor:C.warn, width:13, height:13 }} />
          Quality override active (MLRO) — quality check already passed
        </div>
      )}

      {/* Actions */}
      <div style={{ display:"flex", gap:10, marginTop:20, flexWrap:"wrap" }}>
        {/* Filing export buttons */}
        <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
          <span style={{ color:C.textDim, fontSize:10, letterSpacing:"0.08em" }}>EXPORT FOR FILING:</span>
          <button onClick={handleBSAXML}
            style={{ background:`${C.accent}15`, border:`1px solid ${C.accent}55`,
              borderRadius:6, color:C.accent, padding:"6px 12px",
              cursor:"pointer", fontSize:11, fontFamily:"'DM Mono',monospace",
              display:"flex", alignItems:"center", gap:5 }}>
            ⬇ BSA XML (FinCEN)
          </button>
          <button onClick={handleGoAMLXML}
            style={{ background:`${C.purple}15`, border:`1px solid ${C.purple}55`,
              borderRadius:6, color:C.purple, padding:"6px 12px",
              cursor:"pointer", fontSize:11, fontFamily:"'DM Mono',monospace",
              display:"flex", alignItems:"center", gap:5 }}>
            ⬇ goAML XML
          </button>
          <button onClick={handleOfficialPDF}
            style={{ background:`${C.gold}15`, border:`1px solid ${C.gold}55`,
              borderRadius:6, color:C.gold, padding:"6px 12px",
              cursor:"pointer", fontSize:11, fontFamily:"'DM Mono',monospace",
              display:"flex", alignItems:"center", gap:5 }}>
            📄 Official Form 111 PDF
          </button>
        </div>
        <button onClick={handleDownload}
          style={{ padding:"10px 20px", borderRadius:8, cursor:"pointer", fontSize:13,
            fontFamily:"'DM Mono',monospace", background:"transparent",
            border:`1px solid ${C.border}`, color:C.textMid }}>
          {t('sar.form.downloadPDF')}
        </button>
        <button onClick={()=>handleSave("draft")} disabled={saving}
          style={{ padding:"10px 20px", borderRadius:8, cursor:saving?"wait":"pointer",
            fontSize:13, fontFamily:"'DM Mono',monospace", background:"transparent",
            border:`1px solid ${C.accent}`, color:C.accent }}>
          {saving ? t('sar.form.saving') : t('sar.form.saveDraft')}
        </button>
        {/* Analysts: Submit to MLRO (E-034) or Submit for Review */}
        {!isCO && !isUnderReview && (
          <>
            <button onClick={handleSubmitToMLRO} disabled={saving || overdueBlocked}
              title={overdueBlocked ? "Deadline breached — MLRO acknowledgment required" : ""}
              style={{ padding:"10px 20px", borderRadius:8, cursor:(saving||overdueBlocked)?"not-allowed":"pointer",
                fontSize:13, fontFamily:"'DM Mono',monospace", background:`${C.warn}20`,
                border:`1px solid ${C.warn}`, color:C.warn, fontWeight:600, opacity:(saving||overdueBlocked)?0.45:1 }}>
              {saving ? t('sar.form.saving') : "↑ Submit to MLRO"}
            </button>
            <button onClick={handleSubmitForReview} disabled={saving || overdueBlocked}
              title={overdueBlocked ? "Deadline breached — MLRO acknowledgment required" : ""}
              style={{ padding:"10px 20px", borderRadius:8, cursor:(saving||overdueBlocked)?"not-allowed":"pointer",
                fontSize:13, fontFamily:"'DM Mono',monospace", background:C.accent,
                border:"none", color:C.bg, fontWeight:600, opacity:(saving||overdueBlocked)?0.45:1 }}>
              {saving ? t('sar.form.saving') : t('sar.form.submitForReview')}
            </button>
          </>
        )}
        {/* COs: Submit for Review + Approve & File (quality-gated + deadline-gated) */}
        {isCO && !isUnderReview && (
          <>
            <button onClick={handleSubmitForReview} disabled={saving || overdueBlocked}
              title={overdueBlocked ? "Deadline breached — MLRO acknowledgment required" : ""}
              style={{ padding:"10px 20px", borderRadius:8, cursor:(saving||overdueBlocked)?"not-allowed":"pointer",
                fontSize:13, fontFamily:"'DM Mono',monospace", background:"transparent",
                border:`1px solid ${C.accent}`, color:C.accent, fontWeight:600, opacity:(saving||overdueBlocked)?0.45:1 }}>
              {saving ? t('sar.form.saving') : t('sar.form.submitForReview')}
            </button>
            <button onClick={handleCOApprove}
              disabled={saving || overdueBlocked || !qualityGateOk}
              title={overdueBlocked ? "Deadline breached — MLRO acknowledgment required" : (!qualityGateOk ? "SAR narrative must pass quality check before filing. Run quality check above." : "")}
              style={{ padding:"10px 20px", borderRadius:8,
                cursor:(saving || overdueBlocked || !qualityGateOk) ? "not-allowed" : "pointer",
                fontSize:13, fontFamily:"'DM Mono',monospace",
                background:(!qualityGateOk) ? `${C.success}40` : C.success,
                border:"none", color:C.bg, fontWeight:600,
                opacity:(saving || overdueBlocked || !qualityGateOk) ? 0.5 : 1 }}>
              ✓ File SAR{overdueBlocked ? " (Ack. Required)" : (!qualityGateOk ? " (Quality Check Required)" : "")}
            </button>
          </>
        )}
        {/* COs/MLRO: review actions when SAR submitted for review or MLRO review */}
        {isCO && isUnderReview && (
          <>
            {isMlroReview && (
              <span style={{ padding:"6px 12px", fontSize:11, color:C.warn,
                border:`1px solid ${C.warn}55`, borderRadius:6 }}>
                MLRO Review Required
              </span>
            )}
            <button onClick={handleCOApprove}
              disabled={saving || overdueBlocked || !qualityGateOk}
              title={overdueBlocked ? "Deadline breached — MLRO acknowledgment required" : (!qualityGateOk ? "SAR narrative must pass quality check before filing. Run quality check above." : "")}
              style={{ padding:"10px 20px", borderRadius:8,
                cursor:(saving || overdueBlocked || !qualityGateOk) ? "not-allowed" : "pointer",
                fontSize:13, fontFamily:"'DM Mono',monospace",
                background:(!qualityGateOk) ? `${C.success}40` : C.success,
                border:"none", color:C.bg, fontWeight:600,
                opacity:(saving || overdueBlocked || !qualityGateOk) ? 0.5 : 1 }}>
              ✓ File SAR{overdueBlocked ? " (Ack. Required)" : (!qualityGateOk ? " (Quality Check Required)" : "")}
            </button>
            <button onClick={handleCORequestInfo} disabled={saving}
              style={{ padding:"10px 20px", borderRadius:8, cursor:saving?"wait":"pointer",
                fontSize:13, fontFamily:"'DM Mono',monospace", background:"transparent",
                border:`1px solid ${C.warn}`, color:C.warn, fontWeight:600 }}>
              ↩ Request More Info
            </button>
            <button onClick={handleCODeny} disabled={saving}
              style={{ padding:"10px 20px", borderRadius:8, cursor:saving?"wait":"pointer",
                fontSize:13, fontFamily:"'DM Mono',monospace", background:`${C.danger}15`,
                border:`1px solid ${C.danger}55`, color:C.danger, fontWeight:600 }}>
              ✕ Deny
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── SAR List ─────────────────────────────────────────────────────────────────
export default function SARPage({ currentUser, company, alerts, team }) {
  const C = useTheme();
  const { t } = useTranslation();
  const STATUS_COLOR = {
    draft:       C.warn,
    review:      C.accent,
    mlro_review: C.warn,
    filed:       C.success,
    submitted:   C.success,
    void:        C.textDim,
    amendment_draft: C.accent,
  };
  const [sars,        setSars]       = useState([]);
  const [loading,     setLoading]    = useState(true);
  const [editing,     setEditing]    = useState(null);
  const [confirm,     setConfirm]    = useState(null);
  const [showAudit,   setShowAudit]  = useState(false);
  const [assigning,   setAssigning]  = useState(null);
  const [showOverdue, setShowOverdue]= useState(false);
  const { log }                      = useAuditLog(company, currentUser);

  useEffect(() => {
    if (!company?.id) {
      console.log('[SARPage] No company ID, skipping load');
      return;
    }
    setLoading(true);
    console.log('[SARPage] Loading SARs for company:', company.id);
    db?.from("sar_drafts").select()
      .eq("company_id", company.id)
      .order("updated_at", { ascending: false })
      .then(async ({ data, error }) => {
        console.log('[SARPage] Query result:', { data, error });
        const loaded = data || [];

        // Load assignments for all SARs
        if (loaded.length > 0) {
          try {
            const { data: assignments } = await db?.from("assignments").select()
              .eq("company_id", company.id)
              .in("sar_id", loaded.map(s => s.id))
              .execute();

            if (assignments && assignments.length > 0) {
              const assignmentMap = {};
              assignments.forEach(a => {
                assignmentMap[a.sar_id] = a.assigned_to;
              });
              // Merge assigned_to into SARs
              const withAssignments = loaded.map(s => ({
                ...s,
                assigned_to: assignmentMap[s.id] || null
              }));
              setSars(withAssignments);
            } else {
              setSars(loaded);
            }
          } catch (e) {
            console.warn('[SARPage] Failed to load assignments:', e.message);
            setSars(loaded);
          }
        } else {
          setSars(loaded);
        }
        setLoading(false);
        // F-011 / E-031: Create overdue notifications for breached deadlines
        const overdue = loaded.filter(s => {
          if (s.status === "filed" || s.status === "void" || !s.detection_date) return false;
          const dl = computeDeadline(s.detection_date, s.has_suspect !== false);
          return dl && dl < new Date();
        });
        if (overdue.length > 0 && company?.id && currentUser?.id) {
          const cos = (typeof team !== "undefined" ? (team || []) : [])
            .filter(m => m.permission === "compliance_officer" || m.permission === "admin");
          for (const co of cos) {
            if (!co.id) continue;
            db?.from("notifications").insert({
              company_id:  company.id,
              user_id:     co.user_id || co.id,
              type:        "sar_overdue",
              title:       `⚠ ${overdue.length} SAR${overdue.length > 1 ? "s" : ""} past filing deadline`,
              body:        `${overdue.map(s => s.subject_name || s.id.slice(0,8)).join(", ")} — immediate action required.`,
              link_module: "sar",
              read:        false,
            }).then(() => {}).catch(() => {});
          }
        }
      });
  }, [company?.id]);

  function handleSaved(sar) {
    if (!sar) { setEditing(null); return; }
    setSars(prev => {
      const exists = prev.find(s => s.id === sar.id);
      return exists ? prev.map(s => s.id === sar.id ? sar : s) : [sar, ...prev];
    });
    setEditing(null);
  }

  async function handleAssignSAR(sarId, memberId, sarSubject) {
    if (!company?.id) return;

    setSars(prev => prev.map(s => s.id === sarId ? { ...s, assigned_to: memberId || null } : s));
    setAssigning(null);

    if (memberId) {
      // Insert assignment record
      try {
        const { error } = await db?.from("assignments").insert({
          company_id: company.id,
          assigned_by: currentUser?.id || null,
          assigned_to: memberId,
          sar_id: sarId,
          assignment_type: "sar",
          notes: sarSubject || "SAR",
        }).execute();

        if (error) {
          console.error('[SAR] Failed to assign SAR:', error);
          return;
        }
      } catch (e) { console.warn("[assign]", e.message); }
    } else {
      // Unassign: delete assignment record
      try {
        await db?.from("assignments")
          .delete()
          .eq("sar_id", sarId)
          .eq("company_id", company.id)
          .execute();
      } catch (e) { console.warn("[unassign]", e.message); }
    }
    try {
      await db?.from("notifications").insert({
        company_id:  company.id,
        user_id:     memberId,
        type:        "alert_assigned",
        title:       `SAR assigned: ${sarSubject || sarId.slice(0, 20)}`,
        body:        `You have been assigned a Suspicious Activity Report for review. Open SAR Filing to view it.`,
        link_module: "sar",
        link_id:     sarId,
      }).execute();
    } catch (e) { console.warn("[notify] sar notification failed:", e.message); }
  }

  function triggerVoid(sar) {
    setConfirm({
      title: t('sar.list.voidDialogTitle'),
      body:  t('sar.list.voidDialogBody'),
      confirmLabel: t('sar.list.voidDialogConfirm'),
      onConfirm: async () => {
        await db?.from("sar_drafts").update({ status:"void" }).eq("id", sar.id).execute();
        await log({ action:"sar_voided", module:"sar", entityId:sar.id, entityName:sar.subject_name });
        setSars(prev => prev.map(s => s.id === sar.id ? { ...s, status:"void" } : s));
        setConfirm(null);
      },
    });
  }

  function handleExportCSV() {
    downloadCSV(sars.map(s => ({
      id:           s.id,
      subject:      s.subject_name,
      amount:       s.amount,
      currency:     s.currency,
      status:       s.status,
      jurisdiction: s.jurisdiction,
      created_at:   s.created_at?.slice(0,10),
      filed_at:     s.filed_at?.slice(0,10) || "",
      filed_ref:    s.filed_ref || "",
    })), `crossregguard_sar_${Date.now()}.csv`);
  }

  // Compute BSA deadline data per SAR
  const sarsWithDeadline = sars.map(s => {
    const dl = computeDeadline(s.detection_date, s.has_suspect !== false);
    return { ...s, _deadline: dl, _badge: deadlineBadge(dl, C) };
  });
  const overdueSARs   = sarsWithDeadline.filter(s => s._badge && s._badge.diff < 0);
  const displayedSARs = showOverdue ? overdueSARs : sarsWithDeadline;

  // Show form
  if (editing !== null) {
    return (
      <SARForm
        draft={editing?.id ? editing : null}
        onSave={handleSaved}
        onCancel={() => setEditing(null)}
        alerts={alerts}
        company={company}
        currentUser={currentUser}
        team={team}
      />
    );
  }

  // SAR list
  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between",
        alignItems:"flex-start", marginBottom:24 }}>
        <div>
          <h1 style={{ color:C.text, fontFamily:"'Syne',sans-serif", fontSize:26,
            fontWeight:700, margin:0 }}>{t('sar.title')}</h1>
          <p style={{ color:C.textMid, margin:"4px 0 0", fontSize:14 }}>
            {t('sar.subtitle')} · {sars.length} {t('common.total').toLowerCase()}
          </p>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={handleExportCSV}
            style={{ padding:"8px 16px", borderRadius:7, cursor:"pointer", fontSize:12,
              background:"transparent", border:`1px solid ${C.border}`,
              color:C.textMid, fontFamily:"'DM Mono',monospace" }}>
            {t('sar.list.exportCSV')}
          </button>
          <button onClick={()=>setShowOverdue(v=>!v)}
            style={{ padding:"8px 16px", borderRadius:7, cursor:"pointer", fontSize:12,
              background: showOverdue ? `${C.danger}22` : "transparent",
              border:`1px solid ${showOverdue ? C.danger : C.border}`,
              color: showOverdue ? C.danger : C.textMid, fontFamily:"'DM Mono',monospace" }}>
            ⚠ Show Overdue{overdueSARs.length > 0 ? ` (${overdueSARs.length})` : ""}
          </button>
          <button onClick={()=>setShowAudit(a=>!a)}
            style={{ padding:"8px 16px", borderRadius:7, cursor:"pointer", fontSize:12,
              background:showAudit?C.accentGlow:"transparent",
              border:`1px solid ${showAudit?C.accent:C.border}`,
              color:showAudit?C.accent:C.textMid, fontFamily:"'DM Mono',monospace" }}>
            {t('sar.list.activity')}
          </button>
          <button onClick={()=>setEditing({})}
            style={{ padding:"8px 20px", borderRadius:7, cursor:"pointer", fontSize:13,
              background:C.accent, border:"none", color:C.bg,
              fontWeight:600, fontFamily:"'DM Mono',monospace" }}>
            {t('sar.list.newSAR')}
          </button>
        </div>
      </div>

      {/* Stats row */}
      {sars.length > 0 && (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:24 }}>
          {[
            { label:t('sar.list.statDraft'),       val: sars.filter(s=>s.status==="draft").length,   color:C.warn    },
            { label:t('sar.list.statUnderReview'), val: sars.filter(s=>s.status==="review").length,  color:C.accent  },
            { label:t('sar.list.statFiled'),       val: sars.filter(s=>s.status==="filed").length,   color:C.success },
            { label:t('sar.list.statVoid'),        val: sars.filter(s=>s.status==="void").length,    color:C.textDim },
          ].map(s=>(
            <div key={s.label} style={{ background:C.surface, border:`1px solid ${C.border}`,
              borderRadius:8, padding:"14px 18px", borderTop:`2px solid ${s.color}` }}>
              <div style={{ color:C.textMid, fontSize:10, letterSpacing:"0.1em" }}>{s.label.toUpperCase()}</div>
              <div style={{ color:s.color, fontFamily:"'Syne',sans-serif",
                fontSize:26, fontWeight:700, marginTop:4 }}>{s.val}</div>
            </div>
          ))}
        </div>
      )}

      {/* Overdue banner */}
      {!loading && overdueSARs.length > 0 && (
        <div style={{ background:`${C.danger}18`, border:`1px solid ${C.danger}44`,
          borderRadius:8, padding:"12px 18px", marginBottom:16,
          display:"flex", alignItems:"center", gap:10,
          color:C.danger, fontSize:13, fontFamily:"'DM Mono',monospace" }}>
          <span style={{ fontSize:16 }}>⚠</span>
          <span>
            <strong>{overdueSARs.length} SAR{overdueSARs.length !== 1 ? "s" : ""}</strong>
            {" "}are past their BSA filing deadline
          </span>
          {!showOverdue && (
            <button onClick={()=>setShowOverdue(true)}
              style={{ marginLeft:"auto", background:`${C.danger}22`,
                border:`1px solid ${C.danger}55`, borderRadius:5,
                color:C.danger, fontSize:11, padding:"4px 12px",
                cursor:"pointer", fontFamily:"'DM Mono',monospace" }}>
              Show Overdue
            </button>
          )}
        </div>
      )}

      {/* List */}
      {loading ? (
        <SkeletonTable rows={4} cols={6} />
      ) : sars.length === 0 ? (
        <EmptyState
          icon="◉"
          title={t('sar.list.emptyTitle')}
          body={t('sar.list.emptyBody')}
          action={t('sar.list.emptyAction')}
          onAction={() => setEditing({})}
        />
      ) : (
        <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, overflow:"hidden" }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr style={{ borderBottom:`1px solid ${C.border}` }}>
                {[t('sar.list.colSubject'),t('sar.list.colAmount'),t('sar.list.colJurisdiction'),t('sar.list.colStatus'),"FILED DATE","BSA DEADLINE",t('sar.list.colAssignedTo'),t('sar.list.colCreated'),t('sar.list.colUpdated'),t('sar.list.colActions')].map(h=>(
                  <th key={h} style={{ padding:"10px 14px", textAlign:"left", color:C.textDim,
                    fontSize:10, letterSpacing:"0.1em", fontFamily:"'DM Mono',monospace" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayedSARs.map(s => (
                <tr key={s.id} style={{ borderBottom:`1px solid ${C.border}` }}
                  onMouseEnter={e=>e.currentTarget.style.background=C.surfaceAlt}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <td style={{ padding:"12px 14px" }}>
                    <div style={{ color:C.text, fontSize:13, fontWeight:500 }}>{s.subject_name || "—"}</div>
                    {s.case_id && <div style={{ color:C.textDim, fontSize:10, marginTop:2 }}>{t('sar.list.casePrefix')}{s.case_id.slice(0,12)}</div>}
                  </td>
                  <td style={{ padding:"12px 14px", color:C.text, fontFamily:"'DM Mono',monospace", fontSize:12 }}>
                    {s.amount ? `${parseFloat(s.amount).toLocaleString()} ${s.currency}` : "—"}
                  </td>
                  <td style={{ padding:"12px 14px", color:C.textMid, fontSize:12 }}>{s.jurisdiction || "—"}</td>
                  <td style={{ padding:"12px 14px" }}>
                    <span style={{ color:STATUS_COLOR[s.status]||C.textDim, fontSize:11,
                      fontWeight:600, textTransform:"uppercase" }}>
                      {s.status}
                    </span>
                    {s.filed_ref && <div style={{ color:C.textDim, fontSize:10, marginTop:2 }}>{t('sar.list.refPrefix')}{s.filed_ref}</div>}
                  </td>
                  {/* E-004: Filing date column */}
                  <td style={{ padding:"12px 14px", color:C.textDim, fontSize:12 }}>
                    {s.status === "filed" && s.updated_at ? (
                      <span style={{ color:C.success }}>{new Date(s.updated_at).toLocaleDateString()}</span>
                    ) : (
                      <span style={{ color:C.textDim }}>—</span>
                    )}
                  </td>
                  <td style={{ padding:"12px 14px" }}>
                    {s._deadline ? (
                      <div>
                        <div style={{ color:C.textDim, fontSize:11, marginBottom:4 }}>
                          {s._deadline.toISOString().slice(0,10)}
                        </div>
                        {s._badge && (
                          <span style={{ background:s._badge.bg, color:s._badge.color,
                            border:`1px solid ${s._badge.color}44`, borderRadius:5,
                            padding:"2px 8px", fontSize:10, fontWeight:700,
                            fontFamily:"'DM Mono',monospace", whiteSpace:"nowrap" }}>
                            {s._badge.text}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span style={{ color:C.textDim, fontSize:11 }}>—</span>
                    )}
                  </td>
                  <td style={{ padding:"8px 14px" }}>
                    <select
                      value={s.assigned_to || ""}
                      onChange={e => handleAssignSAR(s.id, e.target.value || null, s.subject_name)}
                      style={{ background: s.assigned_to ? C.accentGlow : C.bg,
                        border: `1px solid ${s.assigned_to ? C.accent : C.border}`,
                        borderRadius: 5, color: s.assigned_to ? C.accent : C.textDim,
                        fontSize: 11, padding: "4px 8px",
                        fontFamily: "'DM Mono',monospace", cursor: "pointer",
                        outline: "none", maxWidth: 130 }}>
                      <option value="">{t('sar.list.unassigned')}</option>
                      {(team||[]).map(m => (
                        <option key={m.id} value={m.id}>
                          {m.firstName||m.first_name} {m.lastName||m.last_name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding:"12px 14px", color:C.textDim, fontSize:12 }}>{s.created_at?.slice(0,10)}</td>
                  <td style={{ padding:"12px 14px", color:C.textDim, fontSize:12 }}>{s.updated_at?.slice(0,10)}</td>
                  <td style={{ padding:"12px 14px" }}>
                    <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                      <button onClick={()=>setEditing(s)}
                        style={{ padding:"5px 12px", background:C.accentGlow,
                          border:`1px solid ${C.borderLight}`, borderRadius:5,
                          color:C.accent, fontSize:11, cursor:"pointer",
                          fontFamily:"'DM Mono',monospace" }}>
                        {t('sar.list.editBtn')}
                      </button>
                      {/* E-001 / F-012: Amend button for filed SARs */}
                      {(s.status === "filed" || s.status === "submitted") && (
                        <>
                          <button onClick={()=>setEditing({
                            ...s,
                            id: undefined,
                            status: "amendment_draft",
                            parent_sar_id: s.id,
                            filed_ref: "",
                            created_at: undefined,
                            updated_at: undefined,
                          })}
                            style={{ padding:"5px 10px", background:`${C.warn}15`,
                              border:`1px solid ${C.warn}55`, borderRadius:5,
                              color:C.warn, fontSize:11, cursor:"pointer",
                              fontFamily:"'DM Mono',monospace" }}>
                            ✎ Amend
                          </button>
                          {/* CAR: Continuation Activity Report */}
                          <button onClick={()=>setEditing({
                            ...s,
                            id: undefined,
                            status: "draft",
                            parent_sar_id: s.id,
                            report_type: "CAR",
                            filed_ref: "",
                            created_at: undefined,
                            updated_at: undefined,
                          })}
                            style={{ padding:"5px 10px", background:`${C.success}15`,
                              border:`1px solid ${C.success}55`, borderRadius:5,
                              color:C.success, fontSize:11, cursor:"pointer",
                              fontFamily:"'DM Mono',monospace" }}>
                            ⤵ CAR
                          </button>
                        </>
                      )}
                      {s.status !== "void" && (
                        <button onClick={()=>triggerVoid(s)}
                          style={{ padding:"5px 10px", background:"transparent",
                            border:`1px solid ${C.danger}40`, borderRadius:5,
                            color:C.danger, fontSize:11, cursor:"pointer",
                            fontFamily:"'DM Mono',monospace" }}>
                          {t('sar.list.voidBtn')}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAudit && <AuditLogPanel company={company} module="sar" />}

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title}
        body={confirm?.body}
        confirmLabel={confirm?.confirmLabel}
        onConfirm={confirm?.onConfirm}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}